import config from '../config';

/**
 * Checks whether debug mode should be enabled for GA4 DebugView.
 * Enabled if config.GA4_DEBUG_MODE is true, or on localhost / dev, or with ?debug_mode=true in URL.
 */
export function isDebugMode() {
  if (typeof window === 'undefined') return false;
  if (config.GA4_DEBUG_MODE === true) return true;
  return (
    process.env.NODE_ENV !== 'production' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.search.includes('debug_mode=true') ||
    window.location.search.includes('debug=true') ||
    sessionStorage.getItem('ga_debug') === 'true'
  );
}

/**
 * Dynamically initializes the GA4 gtag.js script if GA4_MEASUREMENT_ID is configured in config.js.
 */
export function initGA4() {
  if (typeof window === 'undefined') return;
  const measurementId = config.GA4_MEASUREMENT_ID;
  if (!measurementId) {
    console.warn('[GA4] No GA4_MEASUREMENT_ID found in config.js');
    return;
  }
  if (document.getElementById('ga4-gtag-script')) return; // Avoid duplicate injection

  // 1. Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());

  const debug = isDebugMode();
  const configOptions = {
    send_page_view: true,
  };

  // debug_mode: true is required by Google Analytics for events to show in DebugView
  if (debug) {
    configOptions.debug_mode = true;
  }

  gtag('config', measurementId, configOptions);

  // 2. Inject official Google Tag script
  const script = document.createElement('script');
  script.id = 'ga4-gtag-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  console.log(`%c[GA4]%c Initialized for ID: %c${measurementId}%c (debug_mode: ${debug})`,
    'color:#4285F4;font-weight:bold',
    'color:inherit',
    'color:#34A853;font-weight:bold',
    'color:inherit'
  );
}

/**
 * Sends a custom GA4 event using gtag or dataLayer.
 * @param {string} eventName
 * @param {object} params
 */
export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined') return;

  const debug = isDebugMode();
  const eventPayload = {
    ...params,
    ...(debug ? { debug_mode: true } : {}),
  };

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventPayload);
      console.log(`%c[GA4 Event]%c ${eventName}:`, 'color:#4285F4;font-weight:bold', 'color:inherit', eventPayload);
    } else if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: eventName,
        ...eventPayload,
      });
      console.log(`%c[GA4 dataLayer]%c ${eventName}:`, 'color:#FBBC05;font-weight:bold', 'color:inherit', eventPayload);
    } else {
      console.log(`[GA4 Dry-run] ${eventName}:`, eventPayload);
    }
  } catch (err) {
    console.warn(`[GA4] Error tracking event ${eventName}:`, err);
  }
}

/**
 * Fires the 'trial_start' conversion event when a user subscribes to a product trial.
 * Guaranteed to fire strictly once per user using local deduplication guard.
 *
 * @param {string|number} userId
 * @param {string} planName
 * @param {number} value
 * @param {string} currency
 */
export function fireTrialStartOnce(userId, planName = 'usa_19', value = 19.0, currency = 'USD') {
  const effectiveUserId = userId || localStorage.getItem('userId') || 'current_user';
  const storageKey = `pt_trial_start_fired_${effectiveUserId}`;
  try {
    // Guard: Prevent duplicate firing on page reload or subsequent logins
    if (localStorage.getItem(storageKey)) {
      console.log(`[GA4] trial_start already fired for user: ${effectiveUserId}`);
      return;
    }

    trackEvent('trial_start', {
      user_id: String(effectiveUserId),
      plan: planName,
      value: typeof value === 'number' ? value : parseFloat(value) || 19.0,
      currency: currency || 'USD',
    });

    localStorage.setItem(storageKey, new Date().toISOString());
  } catch (err) {
    console.warn('[GA4] Error firing trial_start event:', err);
  }
}

// Global testing helper for developers (callable from browser console)
if (typeof window !== 'undefined') {
  window.ptFireTrialStart = function (plan = 'usa_19', value = 19.0, currency = 'USD') {
    console.log('[GA4] Manually triggering trial_start test event...');
    trackEvent('trial_start', {
      user_id: 'manual_test_' + Date.now(),
      plan: plan,
      value: value,
      currency: currency,
    });
  };
}
